/* New file: assets/product-dimensions-guide.js
   Custom element for the "dimensions_guide" block's horizontal scroller.
   Pattern matches the theme's own convention (assets/collapsible-tabs.js,
   assets/theme.js AccordionGroup): a guarded customElements.define, no
   framework dependency, no Swiper.

   Scope, stated plainly: this only steps the scroll position and toggles the
   prev/next buttons' disabled state. It does not open/close the accordion —
   that is entirely assets/theme.js's AccordionGroup, already wired via the
   is="accordion-group" attribute on the <details> in the snippet. This file
   never touches that element. */

if (!customElements.get('dimensions-guide-scroll')) {
  customElements.define(
    'dimensions-guide-scroll',
    class DimensionsGuideScroll extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-dim-track]');
        this.prevBtn = this.querySelector('[data-dim-prev]');
        this.nextBtn = this.querySelector('[data-dim-next]');
        if (!this.track || !this.prevBtn || !this.nextBtn) return;

        this.onPrev = () => this.step(-1);
        this.onNext = () => this.step(1);
        this.onScroll = this.updateButtons.bind(this);
        this.onResize = this.updateButtons.bind(this);

        this.prevBtn.addEventListener('click', this.onPrev);
        this.nextBtn.addEventListener('click', this.onNext);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize);

        this.updateButtons();
      }

      disconnectedCallback() {
        if (!this.track) return;
        this.prevBtn.removeEventListener('click', this.onPrev);
        this.nextBtn.removeEventListener('click', this.onNext);
        this.track.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
      }

      columnWidth() {
        const col = this.track.querySelector('[data-dim-col]');
        if (!col) return this.track.clientWidth * 0.8;
        const style = getComputedStyle(this.track.querySelector('.dim__grid'));
        const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
        return col.getBoundingClientRect().width + gap;
      }

      step(direction) {
        const distance = this.columnWidth() * direction;
        const before = this.track.scrollLeft;
        this.track.scrollBy({ left: distance, behavior: 'smooth' });

        // Some engines ignore smooth-scroll on a background tab or under
        // reduced motion; correct on the next two frames if it never moved.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this.track.scrollLeft === before) {
              this.track.scrollLeft = before + distance;
            }
            this.updateButtons();
          });
        });
      }

      updateButtons() {
        const max = this.track.scrollWidth - this.track.clientWidth - 1;
        const atStart = this.track.scrollLeft <= 0;
        const atEnd = this.track.scrollLeft >= max;

        this.prevBtn.disabled = atStart;
        this.prevBtn.setAttribute('aria-disabled', String(atStart));
        this.nextBtn.disabled = atEnd;
        this.nextBtn.setAttribute('aria-disabled', String(atEnd));
      }
    }
  );
}
